import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSelector } from "@/components/ui/language-selector";
import { useScheduledMeetingsCount } from "@/hooks/useScheduledMeetingsCount";
import { Badge } from "@/components/ui/badge";
import { useQuickActionBadges } from "@/hooks/useQuickActionBadges";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import {
  ChartPie,
  ArrowRightLeft,
  Users,
  Landmark,
  FileText,
  CreditCard,
  Search,
  Handshake,
  UserCog,
  Settings,
  ChevronDown,
  ChevronUp,
  ListChecks,
  Folder,
  GitBranch,
  FileCheck,
  BookOpen,
  PlusCircle,
  PenTool,
  Monitor,
  Banknote,
  UserPlus,
  Hammer,
  DollarSign,
  Inbox,
  BarChart3,
  Rocket,
  Sparkles,
  Video,
  FolderOpen,
  CalendarClock,
  Phone,
  ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";

const translations = {
  en: {
    quickActions: "Quick Actions",
    newDeal: "New Deal (Seller/Broker)",
    generateNda: "Generate NDA (Buyer)",
    generateCim: "Generate CIM / Starter Pack",
    updateDealMonitor: "Update Deal Monitor",
    lenderOfferForm: "Lender Offer Form",
    managePartners: "Manage Partner Profiles",
    manageConsultants: "Manage Consultants",
    aiDocuments: "AI Documents",
    marketStudy: "Market Study",
    strategicPlan: "Strategic Plan",
    overview: "Overview",
    dealFlow: "Deal Flow",
    usersClients: "Users & Clients",
    identityVerifications: "Identity Verifications",
    sellers: "Sellers",
    brokers: "Brokers",
    buyersBasicElite: "Buyers (Basic / Elite)",
    byod: "BYOD",
    createUser: "Create User",
    lendersOffers: "Lenders & Offers",
    cimEval: "CIM & Evaluation",
    financePayments: "Finance & Payments",
    internalExperts: "Internal Experts Management",
    expertTasks: "Expert Tasks (Mandates)",
    expertDirectory: "Expert Directory (Verification)",
    externalConsultants: "External Consultants Management",
    businessCommissions: "Business & Commissions",
    consultantDirectory: "Consultant Directory (Verification)",
    staffProductivity: "Staff & Productivity",
    triage: "Deal Triage (New)",
    todoSupervision: "My To-Do (Supervision)",
    pipelineByStatus: "Pipeline by Status",
    loadByAnalyst: "Load by Analyst",
    roleManagement: "Role Management",
    settings: "Settings & Configuration",
    superAdmin: "Super Admin",
    staff: "Staff",
    dashboard: "Dashboard",
    myTodo: "My To-Do",
    dealsInProgress: "Deals in Progress",
    changeStatus: "Change Status",
    goToStaff: "Go to Staff Dashboard",
    meetings: "Meetings & Recordings",
    myAvailability: "My Availability",
  },
  fr: {
    quickActions: "Outils & Actions Rapides",
    newDeal: "Nouveau Deal (Vendeur/Broker)",
    generateNda: "Générer NDA (Acheteur)",
    generateCim: "Générer CIM / Starter Pack",
    updateDealMonitor: "Mettre à jour Deal Monitor",
    lenderOfferForm: "Formulaire Offre Lender",
    managePartners: "Gérer Profils Partenaires",
    manageConsultants: "Gérer Consultants",
    aiDocuments: "Documents IA",
    marketStudy: "Étude de Marché",
    strategicPlan: "Plan Stratégique",
    overview: "Vue d'ensemble",
    dealFlow: "Deal Flow",
    usersClients: "Utilisateurs & Clients",
    identityVerifications: "Vérifications d'identité",
    sellers: "Vendeurs",
    brokers: "Courtiers",
    buyersBasicElite: "Acheteurs (Basic / Elite)",
    byod: "BYOD",
    createUser: "Créer un utilisateur",
    lendersOffers: "Prêteurs & Offres",
    cimEval: "CIM & Évaluation",
    financePayments: "Finance & Paiements",
    internalExperts: "Gestion Experts Internes",
    expertTasks: "Tâches Experts (Mandats)",
    expertDirectory: "Répertoire Experts (Vérification)",
    externalConsultants: "Gestion Consultants Externes",
    businessCommissions: "Apport Affaires & Commissions",
    consultantDirectory: "Répertoire Consultants (Vérification)",
    staffProductivity: "Staff & Productivité",
    triage: "Dossiers à Trier",
    todoSupervision: "Ma To-Do (Supervision)",
    pipelineByStatus: "Pipeline par statut",
    loadByAnalyst: "Répartition par analyste",
    roleManagement: "Gestion des Rôles",
    settings: "Paramètres & Configuration",
    superAdmin: "Super Admin",
    staff: "Staff",
    dashboard: "Tableau de bord",
    myTodo: "Ma To-Do",
    dealsInProgress: "Dossiers en cours",
    changeStatus: "Changer de statut",
    goToStaff: "Aller au Dashboard Staff",
    meetings: "Réunions & Enregistrements",
    myAvailability: "Ma disponibilité",
  },
};

interface QuickAction {
  name: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  badgeKey?: 'triage' | 'nda' | 'lenderOffers' | null;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  isSecondary?: boolean;
  subLinks?: { name: string; href: string; hint?: string }[];
  badge?: number;
}

// Quick Action Component
function QuickActionButton({ action, currentPath, badgeCount }: { action: QuickAction; currentPath: string; badgeCount?: number }) {
  const isActive = action.href === currentPath;
  
  return (
    <NavLink
      to={action.href}
      className={cn(
        "flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 text-center relative",
        isActive
          ? "bg-amber-600 text-white shadow-lg"
          : "bg-amber-500 text-white hover:bg-amber-600 hover:shadow-md"
      )}
    >
      <action.icon className="h-4 w-4" />
      <span className="truncate">{action.name}</span>
      {badgeCount && badgeCount > 0 ? (
        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
          {badgeCount}
        </span>
      ) : null}
    </NavLink>
  );
}

interface NavItemComponentProps {
  item: NavItem;
  currentPath: string;
}

function NavItemComponent({ item, currentPath }: NavItemComponentProps) {
  const [isOpen, setIsOpen] = useState(
    item.subLinks?.some((sub) => currentPath.startsWith(sub.href)) || false
  );

  const isActive =
    item.href === currentPath ||
    (item.subLinks && item.subLinks.some((sub) => currentPath.startsWith(sub.href)));

  const linkClasses = cn(
    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 text-sm font-medium",
    isActive
      ? "bg-gray-700 text-amber-500 font-semibold border-l-4 border-amber-500"
      : item.isSecondary
      ? "text-gray-400 hover:bg-gray-800 hover:text-white"
      : "text-gray-300 hover:bg-gray-800 hover:text-white"
  );

  if (item.subLinks) {
    return (
      <div className="space-y-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(linkClasses, "w-full justify-between")}
        >
          <span className="flex items-center gap-3">
            <item.icon className="h-5 w-5" />
            {item.name}
          </span>
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {isOpen && (
          <div className="ml-5 border-l border-gray-700 pl-4 space-y-1 pt-1">
            {item.subLinks.map((sub) => (
              <NavLink
                key={sub.href}
                to={sub.href}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-1.5 py-2 px-3 text-sm rounded-lg transition-colors duration-200",
                    isActive
                      ? "bg-gray-700 text-white font-medium"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  )
                }
              >
                <span>{sub.name}</span>
                {sub.hint && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info
                          className="h-3.5 w-3.5 text-amber-400/80 hover:text-amber-300 shrink-0"
                          onClick={(e) => e.preventDefault()}
                        />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs whitespace-pre-line">
                        {sub.hint}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink to={item.href} className={linkClasses}>
      <item.icon className="h-5 w-5" />
      <span className="flex-1">{item.name}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <Badge className="h-5 min-w-[20px] flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500 border-0">
          {item.badge}
        </Badge>
      )}
    </NavLink>
  );
}

export default function AdminSidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { language } = useLanguage();
  const currentPath = location.pathname;
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  
  const { count: scheduledMeetingsCount } = useScheduledMeetingsCount(user?.id);
  const actionBadges = useQuickActionBadges();
  
  const t = translations[language as keyof typeof translations] || translations.en;

  // Determine if user is super_admin or staff
  const role = user?.roles?.includes("admin") ? "super_admin" : "staff";

  // Quick Actions
  const quickActions: QuickAction[] = role === 'super_admin' ? [
  { name: t.newDeal, href: "/admin/staff/triage", icon: PlusCircle, badgeKey: 'triage' },
 { name: t.generateNda, href: "/tools/nda-generator", icon: PenTool, badgeKey: 'nda' },
  { name: t.marketStudy, href: "/admin/ai/market-study", icon: BarChart3 },
  { name: t.strategicPlan, href: "/admin/ai/strategic-plan", icon: Rocket },
  { name: t.updateDealMonitor, href: "/listings", icon: Monitor },
  { name: t.lenderOfferForm, href: "/admin/forms/lender-offer", icon: Banknote, badgeKey: 'lenderOffers' },
  { name: t.managePartners, href: "/admin/users?tab=partner", icon: UserPlus },
  { name: t.manageConsultants, href: "/admin/users?tab=consultant", icon: Users },
] : [
  { name: t.newDeal, href: "/staff/triage", icon: PlusCircle, badgeKey: 'triage' },
  { name: t.myTodo, href: "/staff/todo", icon: ListChecks },
  { name: t.triage, href: "/staff/triage", icon: Inbox, badgeKey: 'triage' },
  { name: t.dealsInProgress, href: "/staff/deals", icon: Folder },
  { name: t.pipelineByStatus, href: "/staff/pipeline", icon: GitBranch },
  { name: t.generateNda, href: "/tools/nda-generator", icon: PenTool, badgeKey: 'nda' },
];

  // Super Admin navigation links
  const adminLinks: NavItem[] = [
    { name: t.overview, href: "/admin/overview", icon: ChartPie },
    { name: t.dealFlow, href: "/admin/deal-flow", icon: ArrowRightLeft },
    {
      name: t.usersClients,
      href: "/admin/users",
      icon: Users,
      subLinks: [
        { name: t.sellers, href: "/admin/users/sellers" },
        { name: t.brokers, href: "/admin/users/brokers" },
        { name: t.buyersBasicElite, href: "/admin/users/buyers" },
        {
          name: t.byod,
          href: "/admin/users/byod",
          hint:
            language === "fr"
              ? "Bring Your Own Deal — Acheteurs amenant leur propre cible d'acquisition.\n• Sans forfait : 620$\n• Essential : 360$\n• Essential+ : 300$\nMême formulaire que Sellers Pre-Approval (300$) mais le contexte est un acheteur, pas un vendeur."
              : "Bring Your Own Deal — Buyers bringing their own acquisition target.\n• No plan: $620\n• Essential: $360\n• Essential+: $300\nSame form as Sellers Pre-Approval ($300) but the context is a buyer, not a seller.",
        },
        { name: t.createUser, href: "/admin/create-user" },
      ],
    },
    { name: t.lendersOffers, href: "/admin/lenders", icon: Landmark },
    { name: t.cimEval, href: "/admin/cim-eval", icon: FileText },
    {
      name: t.identityVerifications,
      href: "/staff/verifications",
      icon: ShieldAlert,
      badge: actionBadges.verifications,
    },
    {
      name: t.aiDocuments,
      href: "/admin/ai",
      icon: Sparkles,
      subLinks: [
        { name: t.marketStudy, href: "/admin/ai/market-study" },
        { name: t.strategicPlan, href: "/admin/ai/strategic-plan" },
        { name: language === 'en' ? 'Competition Report' : 'Rapport Concurrence', href: "/admin/ai/competition-report" },
      ],
    },
    { name: t.meetings, href: "/meetings", icon: Video, badge: scheduledMeetingsCount },
    { name: t.financePayments, href: "/admin/finance", icon: CreditCard },
    {
      name: t.internalExperts,
      href: "/admin/experts",
      icon: Hammer,
      subLinks: [
        { name: t.expertTasks, href: "/admin/experts/assignments" },
        { name: t.expertDirectory, href: "/admin/experts/list" },
      ],
    },
    {
      name: t.externalConsultants,
      href: "/admin/consultants",
      icon: DollarSign,
      subLinks: [
        { name: t.businessCommissions, href: "/admin/consultants/performance" },
        { name: t.consultantDirectory, href: "/admin/consultants/list" },
      ],
    },
    {
      name: t.staffProductivity,
      href: "/admin/staff",
      icon: UserCog,
      subLinks: [
        { name: t.triage, href: "/admin/staff/triage" },
        { name: t.todoSupervision, href: "/admin/staff/todo-all" },
        { name: t.pipelineByStatus, href: "/admin/staff/pipeline" },
        { name: t.loadByAnalyst, href: "/admin/staff/load" },
        { name: t.roleManagement, href: "/admin/staff/roles" },
        { name: t.myAvailability, href: "/admin/staff-availability" },
      ],
    },
    { name: language === 'en' ? 'Corporate Documents' : 'Documents Corporatifs', href: "/admin/corporate-docs", icon: FolderOpen },
    { name: language === 'en' ? 'Calendar' : 'Calendrier', href: "/admin/calendar", icon: CalendarClock },
    { name: 'Communications', href: "/admin/communications", icon: Phone },
    { name: language === 'en' ? 'Performance & Search Stats' : 'Performance & Stats', href: "/admin/performance", icon: BarChart3 },
    { name: t.settings, href: "/admin/settings", icon: Settings, isSecondary: true },
  ];

  // Staff navigation links
 const staffLinks: NavItem[] = [
    { name: t.myTodo, href: "/staff/todo", icon: ListChecks },
    { name: t.triage, href: "/staff/triage", icon: Inbox },
    { name: t.dealsInProgress, href: "/staff/deals", icon: Folder },
    { name: t.pipelineByStatus, href: "/staff/pipeline", icon: GitBranch },
    { name: t.identityVerifications, href: "/staff/verifications", icon: ShieldAlert, badge: actionBadges.verifications },
    { name: t.meetings, href: "/meetings", icon: Video, badge: scheduledMeetingsCount },
    { name: t.expertDirectory, href: "/admin/experts/list", icon: Hammer },
    { name: t.consultantDirectory, href: "/admin/consultants/list", icon: DollarSign },
    { name: t.myAvailability, href: "/staff/my-availability", icon: CalendarClock },
  ];

  const links = role === "super_admin" ? adminLinks : staffLinks;
  const filteredQuickActions = role === "super_admin" 
    ? quickActions 
    : quickActions.filter(a => !a.adminOnly);

  const primaryLinks = links.filter((l) => !l.isSecondary);
  const secondaryLinks = links.filter((l) => l.isSecondary);

  return (
    <aside className="w-72 shrink-0 border-r border-gray-800 bg-gray-900 flex flex-col h-screen sticky top-0">
      {/* Logo */}
      <div className="px-4 py-4 border-b border-gray-800">
        <h1 className="text-2xl font-bold">
          <span className="text-amber-500">Acquiz</span>
          <span className="text-white">Group</span>
        </h1>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            {role === "super_admin" ? t.superAdmin : t.staff} {t.dashboard}
          </span>
          <LanguageSelector />
        </div>
      </div>

      {/* Quick Actions - Collapsible */}
      <div className="px-3 py-3 border-b border-gray-800">
        <button
          onClick={() => setQuickActionsOpen(!quickActionsOpen)}
          className="flex items-center justify-between w-full px-3 py-2 text-xs text-gray-500 uppercase font-semibold hover:text-gray-300 transition-colors"
        >
          <span>{t.quickActions}</span>
          {quickActionsOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {quickActionsOpen && (
          <div className="space-y-2 mt-2 max-h-64 overflow-y-auto">
            {filteredQuickActions.map((action) => (
              <QuickActionButton key={action.href} action={action} currentPath={currentPath} badgeCount={action.badgeKey ? actionBadges[action.badgeKey] : 0} />
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {primaryLinks.map((item) => (
          <NavItemComponent key={item.name} item={item} currentPath={currentPath} />
        ))}
      </nav>

      {/* Secondary Links */}
      {secondaryLinks.length > 0 && (
        <div className="border-t border-gray-800 px-3 py-4 space-y-1">
          {secondaryLinks.map((item) => (
            <NavItemComponent key={item.name} item={item} currentPath={currentPath} />
          ))}
        </div>
      )}

      {/* Change Status */}
      <div className="border-t border-gray-800 px-4 py-3">
        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
          {t.changeStatus}
        </p>
        <NavLink
          to="/staff"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
        >
          <UserCog className="h-4 w-4" />
          {t.staff}
        </NavLink>
      </div>

      {/* User Info */}
      <div className="border-t border-gray-800 px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">
            {user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.displayName || user?.email}
            </p>
            <p className="text-xs text-gray-500 capitalize">
              {role.replace("_", " ")}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}